import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild
} from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { TranslateService } from "@ngx-translate/core";
import { FormControl } from "@angular/forms";

import { MatPaginator } from "@angular/material/paginator";
import { Contact } from "@onsip/common/interfaces/contact";
import { HubspotInfo } from "@onsip/common/interfaces/hubspot-info";
import { E164PhoneNumber } from "@onsip/common/libraries/e164-phone-number";
import { SaysoCallHistory } from "@onsip/common/services/api/resources/webCall/web-call";
import { ContactService } from "@onsip/common/services/contact/contact.service";
import { LogService } from "@onsip/common/services/logging";
import { WebCallHistoryService } from "@onsip/common/services/web-call-history.service";
import { HubspotApiService } from "../../shared/components/hubspot/services/hubspot-api.service";
import * as callHistoryConfig from "@onsip/common/services/callHistory/call-history-config";
import { MatTableDataSource } from "@angular/material/table";
import { PAGE_SIZE_OPTIONS } from "../../shared/const";

const debug = false;

@Component({
  selector: "onsip-org-call-history",
  templateUrl: "./org-call-history.component.html",
  styleUrls: ["./org-call-history.component.scss"],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: { class: "onsip-grid-content" },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OrgCallHistoryComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    this.setPaginator(paginator);
  }
  hubspotInfo: HubspotInfo | undefined;
  hubspotPageUrl: string | undefined;
  askToLoadMore = false;
  dataSource = new MatTableDataSource<SaysoCallHistory>([]);
  expandedElement: SaysoCallHistory | undefined = undefined;
  isLoading = true;
  pageSize: number = callHistoryConfig.pageSize;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  search = new FormControl("");
  isMobile!: Observable<boolean>;
  lastFetchedDate!: string;

  /** Column list */
  displayedColumns: Array<string> = ["displayDate", "time", "status", "agent", "feedback"];
  expandedRow!: SaysoCallHistory;

  private totalCalls!: Array<SaysoCallHistory>;
  private showMobile = false;

  private unsubscriber = new Subscription();

  constructor(
    private breakpointObserver: BreakpointObserver,
    private hubspotApiService: HubspotApiService,
    private log: LogService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private webCallHistory: WebCallHistoryService
  ) {}

  ngOnInit(): void {
    this.isMobile = this.breakpointObserver
      .observe(Breakpoints.XSmall)
      .pipe(map(result => result.matches));

    this.webCallHistory.webCallBrowse();
    this.unsubscriber.add(
      this.webCallHistory.state
        .pipe(
          map(callState => callState.webCalls),
          map(saysoCallState =>
            saysoCallState.map(call => {
              call.status =
                call.status === "answered"
                  ? this.translate.instant("ONSIP_I18N.ANSWERED")
                  : this.translate.instant("ONSIP_I18N.MISSED");
              return call;
            })
          )
        )
        .subscribe(saysoCallState => {
          this.isLoading = false;
          // only take calls that are of type topics to show in org-call-history
          this.totalCalls = saysoCallState.filter(call => call.targetType === "topic");
          this.dataSource.data = this.totalCalls;
          this.dataSource.filter = this.search.value || "";
          const lastFetchedCall = this.totalCalls[this.totalCalls.length - 1];
          this.lastFetchedDate = lastFetchedCall
            ? lastFetchedCall.displayDate
            : new Date().toDateString();

          if (!this.webCallHistory.isAllFetched && this.dataSource.paginator) {
            this.canAskToLoadMore();
          }
          this.cdRef.markForCheck();
        })
    );

    this.unsubscriber.add(this.search.valueChanges.subscribe(value => this.applyFilter(value)));

    this.unsubscriber.add(
      this.isMobile.subscribe(result => {
        this.showMobile = result;
        this.dataSource.data = this.totalCalls;
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  showMobileRow(): boolean {
    return this.showMobile;
  }

  showDesktopRow(): boolean {
    return !this.showMobile;
  }

  keepSearching(): void {
    debug && this.log.debug("OrgCallHistoryComponent.keepSearching");
    this.askToLoadMore = false;
    this.webCallHistory.webCallBrowse();
    this.isLoading = true;
  }

  getExpandedDetail(element: SaysoCallHistory): void {
    if (!element) this.expandedElement = undefined;
    this.expandedElement =
      this.expandedElement && this.expandedElement.uuid === element.uuid ? undefined : element;
    if (this.expandedElement && this.expandedElement.hubspotutk) {
      this.hubspotApiService
        .getContactByUserToken(this.expandedElement.hubspotutk)
        .then(contact => {
          let contactToAdd: Contact | undefined, callersName: string | undefined;
          if (contact.firstname) callersName = contact.firstname;
          if (contact.lastname) callersName = callersName + " " + contact.lastname;
          if (contact.phone) {
            const phoneNumber: E164PhoneNumber = new E164PhoneNumber(contact.phone);
            if (phoneNumber.isValid) {
              if (!callersName) callersName = phoneNumber.e164DisplayNumber;
              contactToAdd = ContactService.createCustomPhoneContact(callersName, phoneNumber);
            }
          }
          this.hubspotInfo = {
            callersName,
            contact: contactToAdd,
            companyName: contact.company ? contact.company : undefined,
            email: contact.email ? contact.email : undefined,
            hubspotOwner: contact.hubspotOwnerName ? contact.hubspotOwnerName : undefined,
            phoneNumber: contact.phone ? contact.phone : undefined,
            remoteAor:
              contactToAdd && contactToAdd.e164PhoneNumbers
                ? contactToAdd.e164PhoneNumbers[0]
                : undefined
          };
          this.hubspotPageUrl = `https://app.hubspot.com/contacts/${contact.portalId}/contact/${contact.vid}/`;
        })
        .catch(() => {
          this.hubspotInfo = undefined;
          debug &&
            this.log.error(
              "hubspotApiService.getContactByUserToken failed",
              this.expandedElement ? this.expandedElement.hubspotutk || "" : ""
            );
        })
        .finally(() => this.cdRef.markForCheck());
    } else {
      this.hubspotInfo = undefined;
      this.cdRef.markForCheck();
    }
  }

  private setPaginator(paginator: MatPaginator): void {
    if (paginator && !this.dataSource.paginator) {
      this.dataSource.paginator = paginator;
      if (!this.webCallHistory.isAllFetched) this.canAskToLoadMore();
      this.cdRef.markForCheck();
      this.unsubscriber.add(
        paginator.page
          .pipe(distinctUntilChanged((a, b) => a.pageIndex === b.pageIndex))
          .subscribe(page => {
            if (!this.webCallHistory.isAllFetched) {
              this.canAskToLoadMore();
              if (
                this.search.value === "" &&
                (page.pageIndex + 2) * page.pageSize > page.length && // only fetch when within last 2 pages of table
                (page.previousPageIndex || 0) < page.pageIndex // only fetch on forward browses
              ) {
                if (!this.isLoading) {
                  this.webCallHistory.webCallBrowse();
                  this.isLoading = true;
                }
              }
            }
          })
      );
    }
  }

  private applyFilter(filter: string | null): void {
    if (filter) {
      this.dataSource.filter = filter.trim().toLowerCase();
      this.dataSource.paginator?.firstPage();
      this.canAskToLoadMore();
    } else {
      this.dataSource.filter = "";
    }
  }

  private canAskToLoadMore(): void {
    if (this.dataSource.paginator) {
      this.askToLoadMore =
        this.dataSource.filteredData.length <=
        (this.dataSource.paginator.pageIndex + 1) * this.dataSource.paginator.pageSize;
    }
  }
}
